/**
 * Dropping the decode-time copies every JPEG-baseline colour frame keeps.
 *
 * Ultrasound cine clips are almost always JPEG baseline 8-bit colour, and the
 * dicom-image-loader decodes that one transfer syntax on the MAIN thread
 * through a canvas rather than in a codec worker
 * (`decodeImageFrame.js`, case `1.2.840.10008.1.2.4.50`). The decode leaves
 * three buffers behind on the image it hands to the cache, and the cache is
 * told about one of them:
 *
 *  - `imageFrame.pixelData` — the RGB frame, 3 bytes/pixel. This is what
 *    `sizeInBytes` reports (`createImage.js`), so it is the only thing the
 *    image-cache budget ever sees.
 *  - `imageFrame.imageData` — the RGBA `ImageData` the canvas read back,
 *    4 bytes/pixel (`decodeJPEGBaseline8BitColor.js`). For a GPU-rendered
 *    stack (`useRGBA: false`) `createImage` strips the alpha into a NEW buffer
 *    and repoints `pixelData` at it, but leaves this field holding the old one.
 *  - the decode canvas itself, kept alive by the `getCanvas` closure
 *    `createImage` attaches to every colour image — another w×h×4 backing
 *    store, outside the JS heap where no budget can see it.
 *
 * So a cache that believes it holds 3 bytes/pixel actually holds ~11, and its
 * LRU ceiling stops nothing: a 51-clip echo study warmed in the background
 * climbs straight past the configured cap until `ArrayBuffer` allocation (and
 * then `getImageData` itself) fails, frames stop decoding, and the pane is left
 * on its blur-up placeholder.
 *
 * Neither extra is read again by anything the viewer renders through:
 * `getCanvas` is consulted only by cornerstone's CPU-fallback colour renderer,
 * which treats it as optional and falls back to its own LUT path without it,
 * and nothing reads `imageFrame.imageData` once decode has finished. Dropping
 * both on IMAGE_LOADED makes `sizeInBytes` the truth, so the cache cap means
 * what it says again.
 */
import { Enums, eventTarget } from '@cornerstonejs/core';
/**
 * Release the decode-time duplicates of one loaded image. Returns whether
 * anything was released.
 *
 * The RGBA `ImageData` is dropped only when the image's pixels live in a
 * DIFFERENT buffer. When they share one (the `useRGBA: true` CPU path, where
 * `pixelData` IS the `ImageData`'s bytes) there is no duplicate and the field
 * is left alone. The decode canvas goes with any colour image: it is a copy of
 * pixels the image already holds.
 */
export function compactDecodedColorImage(image) {
    if (!image)
        return false;
    let released = false;
    const frame = image.imageFrame;
    const retainedBuffer = frame?.imageData?.data?.buffer;
    if (frame && retainedBuffer) {
        const pixelBuffer = image.getPixelData?.()?.buffer;
        if (pixelBuffer !== retainedBuffer) {
            delete frame.imageData;
            released = true;
        }
    }
    if (image.color && image.getCanvas) {
        delete image.getCanvas;
        released = true;
    }
    return released;
}
function handleImageLoaded(event) {
    compactDecodedColorImage(event.detail?.image);
}
let subscribed = false;
/**
 * Compact every image as it loads. Idempotent, like the other init-time
 * subscriptions.
 *
 * IMAGE_LOADED is the right seam because it fires for every load path that
 * reaches the cache — stack frames, the background prefetcher, scroll-time
 * loads — before the cache files the image (`imageLoader.js` attaches its
 * listener to the load promise ahead of `cache.putImageLoadObject`).
 */
export function subscribeToColorFrameCompaction() {
    if (subscribed)
        return;
    subscribed = true;
    eventTarget.addEventListener(Enums.Events.IMAGE_LOADED, handleImageLoaded);
}
